import Render from '@Views/components/popin/layout/popin-cancel-rfe.html'
import { Vue, Component, Prop, Watch } from 'vue-property-decorator'
import { user } from '@Store/modules'
import { IOfficeSubstitutes, ISubstitute } from '@Store/types'
import { UserRfeStatus } from '@Enums/index'

interface ICancelRfeOptions {
	choice: string   // 'principal' | 'substitutes'
	reason: string
	substituteIds: string[]
}

@Render
@Component({})
export class PopinCancelRfe extends Vue {

	@Prop({ required: true }) options!: ICancelRfeOptions
	@Prop({ default: false }) submitted: boolean
	@Prop({ default: false }) loading: boolean

	selectedChoice = ''
	reasonText = ''
	currentStep = 1
	selectedSubstitutes: string[] = []

	readonly MAX_LENGTH = 200

	//#region Steps
	get hasSubstitutesStep(): boolean {
		return this.selectedChoice === 'substitutes'
	}

	get steps(): { key: string, number: number, label: string }[] {
		const keys = this.hasSubstitutesStep
			? ['scope', 'substitutes']
			: ['scope', 'recap']
		return keys.map((key, index) => ({
			key,
			number: index + 1,
			label: this.$i18n.t(`vm.components.popin.cancel-rfe.stepper.${key}`).toString()
		}))
	}

	get currentStepKey(): string {
		const steps = this.steps
		const step = steps[this.currentStep - 1] || steps[0]
		return step.key
	}

	stepStatus(step: number): string {
		if (step === this.currentStep) return 'is-current'
		if (step < this.currentStep) return 'is-done'
		return 'is-todo'
	}

	get isFirstStep(): boolean {
		return this.currentStep === 1
	}

	get isLastStep(): boolean {
		return this.currentStep === this.steps.length
	}

	get showConfirm(): boolean {
		return this.isLastStep
	}

	get canGoNext(): boolean {
		if (this.currentStepKey === 'scope') return this.selectedChoice !== ''
		if (this.currentStepKey === 'substitutes') return this.selectedSubstituteIds.length > 0
		return true
	}

	get canConfirm(): boolean {
		if (this.selectedChoice === 'principal') return this.reasonText.trim().length > 0 && !this.isReasonTooLong
		if (this.selectedChoice === 'substitutes') return this.selectedSubstituteIds.length > 0
		return false
	}

	next(): void {
		if (!this.canGoNext) return
		if (this.currentStep < this.steps.length) this.currentStep++
	}

	back(): void {
		if (this.currentStep > 1) {
			this.currentStep--
		} else {
			this.$emit('cancel')
		}
	}

	confirm(): void {
		if (!this.canConfirm) return
		this.$emit('confirm')
	}
	//#endregion

	//#region Labels
	get backLabel(): string {
		return this.isFirstStep
			? this.$i18n.t('vm.components.popin.cancel-rfe.nav.cancel').toString()
			: this.$i18n.t('vm.components.popin.cancel-rfe.nav.back').toString()
	}

	get nextLabel(): string {
		return this.$i18n.t('vm.components.popin.cancel-rfe.nav.next').toString()
	}

	get confirmLabel(): string {
		return this.$i18n.t('vm.components.popin.cancel-rfe.confirm').toString()
	}

	get principalName(): string {
		const coord = user.currentUser && user.currentUser.coordinates
		if (!coord) return ''
		return `${coord.first_name || ''} ${coord.last_name || ''}`.trim()
	}
	//#endregion

	//#region Substitutes
	get officesWithSubstitute(): IOfficeSubstitutes[] {
		const current = user.currentUser
		return (current && current.substitutes || []).filter(office => this.substitutesOf(office).length > 0)
	}

	private isSubstituteActivated(substitute: ISubstitute): boolean {
		return !!substitute.rfe && substitute.rfe.status === UserRfeStatus.Accepted
	}

	private get uniqueSubstitutesByOffice(): { [officeId: string]: ISubstitute[] } {
		const seen = new Set<string>()
		const result: { [officeId: string]: ISubstitute[] } = {}
		const offices = (user.currentUser && user.currentUser.substitutes) || []
		offices.forEach(office => {
			result[office.id] = (office.substitutes || []).filter(sub => {
				if (!this.isSubstituteActivated(sub)) return false
				if (seen.has(sub.id)) return false
				seen.add(sub.id)
				return true
			})
		})
		return result
	}

	substitutesOf(office: IOfficeSubstitutes): ISubstitute[] {
		return this.uniqueSubstitutesByOffice[office.id] || []
	}

	substituteNameWithEmail(substitute: ISubstitute): string {
		return `${substitute.first_name || ''} ${substitute.last_name || ''} - (${substitute.email})`.trim()
	}

	substituteDisplayName(substitute: ISubstitute): string {
		return `${substitute.first_name || ''} ${substitute.last_name || ''}`.trim() || substitute.email
	}

	private substituteKey(ofiId: string, userId: string): string {
		return `${ofiId}::${userId}`
	}

	isSubstituteSelected(ofiId: string, userId: string): boolean {
		return this.selectedSubstitutes.indexOf(this.substituteKey(ofiId, userId)) !== -1
	}

	toggleSubstitute(ofiId: string, userId: string, checked: boolean): void {
		const key = this.substituteKey(ofiId, userId)
		if (checked) {
			if (this.selectedSubstitutes.indexOf(key) === -1) this.selectedSubstitutes.push(key)
		} else {
			this.selectedSubstitutes = this.selectedSubstitutes.filter(k => k !== key)
		}
	}

	get selectedSubstituteIds(): string[] {
		const ids = this.selectedSubstitutes.map(key => key.split('::')[1])
		return Array.from(new Set(ids))
	}

	get selectedSubstituteCount(): number {
		return this.selectedSubstituteIds.length
	}

	officeSelectedCount(office: IOfficeSubstitutes): number {
		return this.substitutesOf(office).filter(sub => this.isSubstituteSelected(office.id, sub.id)).length
	}

	isOfficeAllSelected(office: IOfficeSubstitutes): boolean {
		const subs = this.substitutesOf(office)
		return subs.length > 0 && subs.every(sub => this.isSubstituteSelected(office.id, sub.id))
	}

	get isAllSelected(): boolean {
		const offices = this.officesWithSubstitute
		return offices.length > 0 && offices.every(office => this.isOfficeAllSelected(office))
	}

	toggleAllSubstitutes(): void {
		const selectAll = !this.isAllSelected
		this.officesWithSubstitute.forEach(office => {
			this.substitutesOf(office).forEach(sub => this.toggleSubstitute(office.id, sub.id, selectAll))
		})
	}

	get selectedSubstituteDetails(): { office: IOfficeSubstitutes, substitute: ISubstitute }[] {
		const result: { office: IOfficeSubstitutes, substitute: ISubstitute }[] = []
		this.officesWithSubstitute.forEach(office => {
			this.substitutesOf(office).forEach(sub => {
				if (this.isSubstituteSelected(office.id, sub.id)) result.push({ office, substitute: sub })
			})
		})
		return result
	}
	//#endregion

	//#region Reason
	get isReasonTooLong(): boolean {
		return this.reasonText.length > this.MAX_LENGTH
	}

	get reasonMessage(): string {
		return this.isReasonTooLong
			? this.$i18n.t('vm.components.popin.cancel-rfe.reason.maxLength-warning', [this.MAX_LENGTH]).toString()
			: ''
	}
	//#endregion

	@Watch('selectedChoice')
	onChoiceChange(): void {
		this.options.choice = this.selectedChoice
		this.currentStep = 1
		if (this.selectedChoice !== 'substitutes') this.selectedSubstitutes = []
	}

	@Watch('selectedSubstitutes')
	onSelectedSubstitutesChange(): void {
		this.options.substituteIds = this.selectedChoice === 'substitutes' ? this.selectedSubstituteIds : []
	}

	@Watch('reasonText')
	onReasonChange(): void {
		this.options.reason = this.reasonText.slice(0, this.MAX_LENGTH)
	}
}
